/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Badge,
  UncontrolledTooltip,
  Progress,
  Table,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import BasePagination from './BasePagination';
import { get } from '../../helpers/api_helper';
import { formatTime, getTimeFormat, TIME_FORMAT_TIMESTAMP } from '../../utilities/timeFormat';

const columnDefinition = {
  label: '',
  sort: false,
  link: false,
  format: (x) => x,
  badges: false,
  date: false,
  actions: false,
  translate: true,
  image: false,
  alt: null,
  modcfg: false,
  html: false,
  class: '',
  style: { border: 'none' },
  actionStyle: {},
  progress: false,
  checkbox: false,
};

function BaseGrid(props) {
  const {
    url,
    config,
    hasSearch,
    search,
    list,
    skipSort,
    createText,
    filters,
    ...rest
  } = props;

  let className = 'table table-striped table-bordered table-sm';
  let { keyProp } = props;

  if (rest.className) className = rest.className;

  const { shouldUpdate } = props;

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(config.pagination.perPage);
  const [sort, setSort] = useState(config.pagination.sort);
  const [dir, setDir] = useState(config.pagination.direction);
  const [data, setData] = useState({ results: [], total: 0 });
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState(search);

  if (keyProp == null) keyProp = 'id';

  useEffect(() => {
    const fetchData = async (p, s, d, pp, q, f) => {
      let uri;

      if (q != null) {
        uri = `${url}?page=${p}&sort=${s}&dir=${d}&per=${pp}&q=${q}&f=${encodeURIComponent(f)}`;
      } else {
        uri = `${url}?page=${p}&sort=${s}&dir=${d}&per=${pp}&f=${encodeURIComponent(f)}`;
      }

      if (list !== false) {
        uri = `${uri}&list=${list.join(',')}`;
      }

      if (list !== false && list[0] == null) {
        setLoading(false);
        setData({ total: 0, results: [] });
        setTotalPages(0);
        return;
      }

      try {
        const results = await get(uri);

        setData(results);
        setTotalPages(Math.ceil(results.total / perPage));
        setSort(s);
        setDir(d);
        setPerPage(pp);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchData(currentPage, sort, dir, perPage, query, filters);
  }, [currentPage, sort, dir, query, shouldUpdate, filters, url]);

  const changeSort = (key) => {
    if (sort === key) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(key);
      setCurrentPage(1);
    }
  };

  const searchKeyUp = (event) => {
    if (event.which === 13) {
      setQuery(event.target.value);
      setCurrentPage(1);
    }
  };

  /**
   * Prepare column header from config
   *
   * @param {*} currentSort active sort column key
   * @param {*} currentDir current sort direction
   * @param {*} inc increment
   * @param {*} key data key for intended column
   * @param {*} column the custom column config
   * @returns jsx
   */
  function prepHeader(currentSort, currentDir, inc, key, column) {
    const mc = { ...columnDefinition, ...column };

    let jsx;
    let sortButton;
    const colkey = `gridheader_${key}`;

    if (!skipSort && mc.sort !== false) {
      if (currentSort === key) {
        // this column is the current sort
        const classname = currentDir === 'asc' ? 'bx bx-chevron-up' : 'bx bx-chevron-down';

        sortButton = (
          <strong className={classname} style={{ verticalAlign: 'middle' }} />
        );
      }

      jsx = (
        <th style={mc.style} data-priority={inc} key={colkey} className={mc.class}>
          <Button
            color="link"
            style={
              {
                padding: '0px',
                fontWeight: '600',
              }
            }
            onClick={() => changeSort(key)}
          >
            {props.t(mc.label)}
          </Button>

          {sortButton}
        </th>
      );
    } else {
      jsx = (
        <th
          style={mc.style}
          data-priority={inc}
          key={colkey}
          className={mc.class}
        >
          {mc.label}
        </th>
      );
    }

    return jsx;
  }

  /**
   * Prepare column using config
   */
  function prepColumn(key, column, item) {
    const mc = { ...columnDefinition, ...column };
    let jsx;

    // format value
    let final = mc.format(item[key], item);

    // translate
    if (mc.translate) {
      if (typeof final === 'string') {
        final = props.t(final);
      } else if (Array.isArray(final)) {
        for (let i = 0; i < final.length; i += 1) {
          final[i] = props.t(final[i]);
        }
      }
    }

    // Date
    if (mc.date !== false && item[key] != null) {
      const d = new Date(item[key]);

      d.toLocaleString(process.env.REACT_APP_LOCALE, { timeZone: mc.date.timezone });
      const formatter = getTimeFormat() === TIME_FORMAT_TIMESTAMP
        ? ((date) => formatTime(date))
        : (mc.date.format || ((date) => formatTime(date)));
      final = (
        <>
          <span id={`date_${item[keyProp]}_${key}`}>{formatter(d)}</span>
          <UncontrolledTooltip placement="top" target={`date_${item[keyProp]}_${key}`}>
            {d.toString()}
          </UncontrolledTooltip>
        </>
      );
    }

    // Image
    if (mc.image !== false) {
      const src = mc.image.src(item);

      final = (
        <img alt="thumbnail" src={src} className={mc.image.class} style={mc.image.style} />
      );
    }

    // Progress Bar
    if (mc.progress !== false) {
      const value = mc.progress.value(item);

      let color = 'primary';
      if (typeof mc.progress.color === 'function') {
        color = mc.progress.color(item);
      } else {
        color = mc.progress.color === undefined ? 'info' : mc.progress.color;
      }

      let animated = mc.progress.animated === undefined ? true : mc.progress.animated;

      if (value === 100) {
        color = 'success';
        animated = false;
      }

      final = (
        <Progress
          striped={mc.striped === undefined ? true : mc.striped}
          animated={animated}
          color={color}
          value={value}
          style={{ backgroundColor: '#e3e2e2' }}
        />
      );
    }

    // Badges
    if (mc.badges !== false) {
      let shrap = final;

      if (final !== null) {
        if (typeof mc.badges.split === 'string') {
          shrap = final.toString().split(mc.badges.split);
        }

        final = [];

        for (let i = 0; i < shrap.length; i += 1) {
          final.push(
            <span key={`badge_${item.id}_${i}`} className={`badge ${mc.badges.class}`}>
              {shrap[i]}
            </span>,
            <span key={`badge_${item.id}_${i}_ws`}> </span>, // add whitespace
          );
        }
      }
    }

    // html
    if (mc.html !== false) {
      // eslint-disable-next-line react/no-danger
      final = <div dangerouslySetInnerHTML={{ __html: final }} />;
    }

    // Checkbox
    if (mc.checkbox !== false) {
      final = (
        <input
          type="checkbox"
          aria-label={props.t(mc.checkbox.label || mc.label)}
          checked={mc.checkbox.checked(item)}
          onChange={(event) => mc.checkbox.change(item, event.target.checked)}
        />
      );
    }

    // Alt
    if (mc.alt !== null) {
      final = (
        <>
          <span id={`alt_${item[keyProp]}_${key}`}>{final}</span>
          <UncontrolledTooltip placement="top" target={`alt_${item[keyProp]}_${key}`}>
            {item[mc.alt]}
          </UncontrolledTooltip>
        </>
      );
    }

    jsx = <td className="align-middle" style={mc.style} key={key}>{final}</td>;

    // Actions
    if (mc.actions !== false) {
      jsx = [];
      for (let i = 0; i < mc.actions.length; i += 1) {
        if (mc.actions[i].condition === undefined || mc.actions[i].condition(item) === true) {
          jsx.push(
            <li key={`li_${item.id}_${mc.actions[i].key}`}>
              <Button
                id={`act_btn_${item.id}_${mc.actions[i].key}`}
                alt={mc.actions[i].alt}
                style={mc.actions[i].actionStyle}
                className={mc.actions[i].classFor
                  ? mc.actions[i].classFor(item) : mc.actions[i].class}
                disabled={(mc.actions[i].disabled)}
                onClick={(event) => mc.actions[i].click(item, event)}
              >
                <span className={mc.actions[i].icon} />
              </Button>
            </li>,
            <UncontrolledTooltip key={mc.actions[i].key} placement="top" target={`act_btn_${item.id}_${mc.actions[i].key}`}>
              {mc.actions[i].alt}
            </UncontrolledTooltip>,
          );
        }
      }

      jsx = (
        <td className="align-middle" style={{ border: 'none' }} key={key}>
          <ul key={`ul_${item.id}`} className="list-unstyled hstack gap-1 mb-0">
            {jsx}
          </ul>
        </td>
      );
    }

    // Links
    if (mc.link !== false) {
      if (mc.link.func !== undefined) {
        const lnkUrl = mc.link.func(item);
        jsx = (
          <td className="align-middle" key={key}>
            <a
              href={lnkUrl}
            >
              {final}
            </a>
          </td>
        );
      } else if (mc.link.click !== undefined) {
        jsx = (
          <td className="align-middle" key={key}>
            <Button
              color="link"
              className="p-1"
              onClick={() => mc.link.click(item)}
            >
              {final}
            </Button>
          </td>
        );
      } else if (mc.link.condition === undefined || mc.link.condition(item) !== false) {
        if (mc.link.external !== true) {
          jsx = (
            <td className="align-middle" style={{ border: 'none' }} key={key}>
              <Link
                to={mc.link.to.replace(/%(.+?)%/g, (mat, grp) => `${item[grp]}`)}
              >
                {final}
              </Link>
            </td>
          );
        } else {
          jsx = (
            <td style={{ border: 'none' }} key={key}>
              <a
                target="_blank"
                rel="noreferrer"
                href={mc.link.to.replace(/%(.+?)%/g, (mat, grp) => `${item[grp]}`)}
              >
                {final}
              </a>
            </td>
          );
        }
      }
    }

    return jsx;
  }

  let searchField;
  let searchQuery;
  let createButton;

  if (hasSearch) {
    searchField = (
      <Col md="4">
        <div className="app-search d-lg-block">
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder={props.t('Search...')}
              onKeyUp={searchKeyUp}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  return false;
                }

                return true;
              }}
            />
            <span className="bx bx-search-alt" />
          </div>
        </div>
      </Col>
    );

    if (query != null) {
      searchQuery = (
        <Badge style={{ fontSize: '12px', padding: '5px' }} color="info">
          <strong className="bx bx-search" />
          &nbsp;&nbsp;
          {query}
          &nbsp;&nbsp;
          <span style={{ cursor: 'pointer' }} onClick={() => { setQuery(null); return false; }} className="mdi mdi-close-thick" />
        </Badge>
      );
    }
  }

  if (props.create != null && typeof props.create === 'string') {
    createButton = (
      <Link to={props.create} className="btn btn-secondary btn-sm">
        <span className="bx bx-plus-medical" />
        &nbsp;
        <span>{props.t(createText)}</span>
      </Link>
    );
  } else if (props.create != null && typeof props.create === 'function') {
    createButton = (
      <Button className="btn btn-sm btn-primary" onClick={props.create}>
        <span className="bx bx-plus" />
        &nbsp;
        <span>{props.t(createText)}</span>
      </Button>
    );
  }

  let tableBody;
  if (loading === true) {
    tableBody = (
      <tr>
        <td colSpan={99} style={{ border: 'none' }}>
          <div className="text-center">
            <Spinner style={{ width: '4rem', height: '4rem' }} type="border" color="info" />
          </div>
        </td>
      </tr>
    );
  } else if (data.total === 0 && loading === false) {
    if (error !== false) {
      tableBody = (
        <tr>
          <td style={{ border: 'none' }} colSpan={99}>
            <Alert color="danger" className="mt-3" isOpen>
              {error}
            </Alert>
          </td>
        </tr>
      );
    } else {
      tableBody = (
        <tr>
          <td style={{ border: 'none' }} colSpan={99}>
            <p>{props.t('No Results.')}</p>
          </td>
        </tr>
      );
    }
  } else {
    tableBody = (
      data.results.map((item) => (
        <tr className={config.rowClass !== undefined ? config.rowClass(item) : ''} key={item[keyProp]}>
          {
            Object.keys(config.columns).map((x) => (
              prepColumn(x, config.columns[x], item)
            ))
          }
        </tr>
      ))
    );
  }

  const jsx = (
    <div>
      <Row>
        <Col md={hasSearch ? '8' : '12'}>
          <div className={hasSearch ? 'hstack gap-3 mt-4' : 'hstack gap-3 mb-2'}>
            {props.shouldPaginate && data.total > 0 && (
              <div className="bg-light border text" style={{ padding: '4px' }}>
                {`${Math.floor(perPage * (currentPage - 1) + 1).toString()}-`}
                {`${Math.floor(perPage * (currentPage - 1) + data.results.length).toString()} of ${data.total}`}
              </div>
            )}
            {createButton}
            {searchQuery}
          </div>
        </Col>

        {searchField}
      </Row>

      <div className="table-rep-plugin">
        <div
          className="table-responsive mb-0"
          data-pattern="priority-columns"
        >
          <Table
            className={className}
          >
            <thead>
              <tr>
                {
                  Object.keys(config.columns).map((x, i) => (
                    prepHeader(sort, dir, i, x, config.columns[x])
                  ))
                }
              </tr>
            </thead>
            <tbody>
              {tableBody}
            </tbody>
          </Table>
          {props.shouldPaginate && (
            <div className="float-end">
              <BasePagination
                lastPage={totalPages}
                currentPage={currentPage}
                switchPage={(page) => setCurrentPage(page)}
                showSkip
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return jsx;
}

BaseGrid.defaultProps = {
  shouldUpdate: 0,
  shouldPaginate: true,
  hasSearch: false,
  create: false,
  createText: 'New',
  keyProp: 'id',
  search: null,
  t: (x) => (x),
  list: false,
  skipSort: false,
  filters: '',
};

BaseGrid.propTypes = {
  url: PropTypes.string.isRequired,
  hasSearch: PropTypes.bool,
  create: PropTypes.any,
  createText: PropTypes.string,
  keyProp: PropTypes.string,
  search: PropTypes.string,
  config: PropTypes.object.isRequired,
  t: PropTypes.any,
  shouldUpdate: PropTypes.number,
  shouldPaginate: PropTypes.bool,
  list: PropTypes.any,
  filters: PropTypes.string,
  skipSort: PropTypes.bool,
};

export default connect(() => ({

}), {})(withTranslation()(BaseGrid));
